import { useMutation, useQuery } from "@tanstack/react-query";
import { triggerDownload } from "../lib/export-download";
import { downloadExportBundle, getExportManifest } from "./runs-api";
import { downloadSolverInputs } from "./solver-input-export";
import type { ExportManifestDto, ExportType } from "../types";

/**
 * TanStack Query cache key for the Phase 6 export manifest. Scoped to a
 * `(scheduleId, runId)` pair so re-opening the modal against a different run
 * (after a refresh that produces a new latest run, say) refetches cleanly.
 * Pattern mirrors {@link REASSIGN_OPTIONS_KEY}.
 */
export const EXPORT_MANIFEST_KEY = (
  scheduleId: string,
  runId: string | undefined,
) => ["schedule-export-manifest", scheduleId, runId] as const;

/**
 * Query hook for the Phase 6 export manifest. Gated on `scheduleId`, `runId`,
 * and the optional `enabled` flag so the export modal can fetch only on open.
 *
 * The `queryFn` re-checks `runId` at runtime — even though the `enabled` gate
 * keeps the query off the wire when `runId` is undefined, narrowing here lets
 * us pass a non-null string to {@link getExportManifest} without an `as string`
 * cast. See `docs/solutions/test-failures/unsafe-as-cast-in-tanstack-queryfn.md`
 * for the Phase 5 lesson this pattern encodes.
 *
 * Stale time is 30 seconds — the manifest only changes if a new run is created
 * or feature flags are toggled, neither of which happens during a typical
 * modal-open session, but a short stale lets us refetch on re-focus after the
 * user has been away.
 */
export function useExportManifest(
  scheduleId: string,
  runId: string | undefined,
  options?: { enabled?: boolean },
) {
  const callerEnabled = options?.enabled ?? true;
  const enabled = !!scheduleId && !!runId && callerEnabled;
  return useQuery<ExportManifestDto>({
    queryKey: EXPORT_MANIFEST_KEY(scheduleId, runId),
    queryFn: () => {
      if (!runId) {
        throw new Error(
          "useExportManifest called with undefined runId — enabled gate violated",
        );
      }
      return getExportManifest(scheduleId, runId);
    },
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Mutation hook that downloads one export bundle and writes it to disk via
 * `triggerDownload`. Each call site gets its own per-type mutation instance —
 * the export modal calls this 4 times (once per card) so each card can show
 * its own `isPending` spinner and `error` message independently, supporting
 * concurrent downloads (per design D19, the modal stays open after a download
 * and other cards remain clickable).
 *
 * Errors thrown by {@link downloadExportBundle} are `Error` instances with a
 * user-facing message extracted from the backend's `ApiResponse<object>`
 * failure body — the modal surfaces them in the card's red error line.
 *
 * Pattern intentionally mirrors `useApplyOverride`: the hook itself is
 * cache-invalidation-free (downloads don't mutate server state), but otherwise
 * the same `useMutation<void, Error>` shape so call sites use the familiar
 * `isPending` / `mutate` / `onError` API.
 */
export function useDownloadExport(
  scheduleId: string,
  runId: string,
  type: ExportType,
) {
  // The mutate variable carries the optional organizer override (only the
  // overall exports use it — see downloadExportBundle). `void` keeps the
  // existing call sites (per-teacher/-student/-room) working with a bare
  // `mutate()`.
  return useMutation<void, Error, { organizer?: string } | void>({
    mutationFn: async (vars) => {
      const { blob, filename } = await downloadExportBundle(
        scheduleId,
        runId,
        type,
        vars?.organizer,
      );
      triggerDownload(blob, filename);
    },
  });
}

/**
 * Mutation hook that downloads the schedule's solver-input workbook (.xlsx) and
 * writes it to disk via `triggerDownload`. Unlike {@link useDownloadExport},
 * this is schedule-scoped (no run) and **not** run-gated — it works even before
 * the first scheduler run.
 *
 * Errors thrown by {@link downloadSolverInputs} are `Error` instances carrying a
 * user-facing message extracted from the backend's `ApiResponse<object>` body
 * (or a friendly 403 string). The toolbar button surfaces them via `onError`.
 *
 * `useMutation` alone does NOT prevent a second concurrent `mutate()`, so call
 * sites must still guard double-clicks (`disabled={mutation.isPending}` plus an
 * early return). See `solver-input-export-button.tsx`.
 */
export function useDownloadSolverInputs(scheduleId: string) {
  return useMutation<void, Error, void>({
    mutationFn: async () => {
      const { blob, filename } = await downloadSolverInputs(scheduleId);
      triggerDownload(blob, filename);
    },
  });
}
